/* eslint-disable @typescript-eslint/ban-types */
import { useSubscriptionByMerchantQuery } from '@src/reducers/merchantApi';
import { useGetPartnerConfigQuery } from '@src/reducers/partnerApi';
import { useMemo } from 'react';

export type SubscriptionsConditionsProps = {
  subscriptionState: 'TRIAL' | 'ACTIVE';
  billingType: 'AUTO' | 'MANUAL';
  paymentType: 'TC' | 'OTHER';
  partnerBillingType: 'b2chat_billing' | 'partner_billing';
};

export type UseSubscriptionsConditionsResult = {
  isLoading: boolean;
  showSubscribeLink: boolean;
  showPlanSection: boolean;
  showTCSection: boolean;
  showPaymentSection: boolean;
  showCancelSection: boolean;
};

const showAll = {
  showSubscribeLink: true,
  showPlanSection: true,
  showTCSection: true,
  showPaymentSection: true,
  showCancelSection: true,
};

const hideAll = {
  showSubscribeLink: false,
  showPlanSection: false,
  showTCSection: false,
  showPaymentSection: false,
  showCancelSection: false,
};

type Rule = Partial<UseSubscriptionsConditionsResult> & {
  condition: [
    subscriptionState: 'TRIAL' | 'ACTIVE',
    billingType: 'AUTO' | 'MANUAL',
    paymentType: 'TC' | 'OTHER'
  ];
};

const RULES = {
  partner_billing: [
    {
      condition: ['TRIAL', 'AUTO', 'TC'],
      showCancelSection: true,
    },
    {
      condition: ['TRIAL', 'MANUAL', 'TC'],
      ...hideAll,
    },
    {
      condition: ['TRIAL', 'MANUAL', 'OTHER'],
      ...hideAll,
    },
    {
      condition: ['ACTIVE', 'AUTO', 'TC'],
      showCancelSection: true,
    },
    {
      condition: ['ACTIVE', 'AUTO', 'OTHER'],
      showCancelSection: true,
    },
    {
      condition: ['ACTIVE', 'MANUAL', 'TC'],
      showCancelSection: true,
    },
    {
      condition: ['ACTIVE', 'MANUAL', 'OTHER'],
      showCancelSection: true,
    },
  ] as Rule[],
  b2chat_billing: [
    {
      condition: ['TRIAL', 'AUTO', 'TC'],
      ...showAll,
    },
    {
      condition: ['TRIAL', 'MANUAL', 'TC'],
      ...showAll,
    },
    {
      condition: ['TRIAL', 'MANUAL', 'OTHER'],
      ...showAll,
    },
    {
      condition: ['ACTIVE', 'AUTO', 'TC'],
      ...showAll,
    },
    {
      condition: ['ACTIVE', 'AUTO', 'OTHER'],
      showCancelSection: true,
    },
    {
      condition: ['ACTIVE', 'MANUAL', 'TC'],
      showCancelSection: true,
    },
    {
      condition: ['ACTIVE', 'MANUAL', 'OTHER'],
      showCancelSection: true,
    },
  ] as Rule[],
} as const;

export function useSubscriptionsConditions(): UseSubscriptionsConditionsResult {
  const subscriptionQuery = useSubscriptionByMerchantQuery();
  const partnerConfigQuery = useGetPartnerConfigQuery();

  const isLoading = subscriptionQuery.isLoading || partnerConfigQuery.isLoading;

  const partnerBillingType =
    partnerConfigQuery.data?.billingType || 'b2chat_billing';

  const subscriptionState =
    subscriptionQuery.data?.merchant.state === 'ACTIVE' ||
    subscriptionQuery.data?.merchant.state === 'CANCELLATIONREQUEST'
      ? 'ACTIVE'
      : 'TRIAL';

  const billingType = subscriptionQuery.data?.automaticBilling
    ? 'AUTO'
    : 'MANUAL';

  const paymentType = subscriptionQuery.data?.paymentType.startsWith('TC')
    ? 'TC'
    : 'OTHER';

  const conditions = isLoading ? undefined : RULES[partnerBillingType];

  const rule =
    conditions?.find(
      ({ condition }) =>
        condition[0] === subscriptionState &&
        condition[1] === billingType &&
        condition[2] === paymentType
    ) || hideAll;

  return useMemo(
    () => ({
      isLoading,
      showSubscribeLink: rule.showSubscribeLink || false,
      showPlanSection: rule.showPlanSection || false,
      showTCSection: rule.showTCSection || false,
      showPaymentSection: rule.showPaymentSection || false,
      showCancelSection: rule.showCancelSection || false,
    }),
    [
      isLoading,
      rule.showCancelSection,
      rule.showPaymentSection,
      rule.showPlanSection,
      rule.showSubscribeLink,
      rule.showTCSection,
    ]
  );
}
